import styles from "./ClientCardsList.module.css";
import dangerImage from "../../assets/dangerImage.png";
import { useTranslation } from "react-i18next";
import { useCallback, useEffect, useRef, useState } from "react";
import ClientCard from "../ClientCard";
import api from "../../utils/axios/api";
import { createCardToken, getIdentificationTypes } from "@mercadopago/sdk-react";
import MpCardSecureFields from "../MpCardSecureFields/MpCardSecureFields";

export interface ICard {
  id: string;
  name: string;
  lastFourDigits: string;
  flag: string;
}

const ClientCardsList = () => {
  const { t } = useTranslation();
  const [clientCard, setClientCard] = useState<ICard[]>([]);
  const [showAddCard, setShowAddCard] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const [cardName, setCardName] = useState("");
  const [cardSurname, setCardSurname] = useState("");
  const [identificationTypes, setIdentificationTypes] = useState<
    { id: string; name: string; min_length: number; max_length: number }[]
  >([]);
  const [identificationType, setIdentificationType] = useState("");
  const [identificationNumber, setIdentificationNumber] = useState("");
  const secureFieldsValidRef = useRef({
    cardNumber: false,
    securityCode: false,
    expirationDate: false,
  });

  const onMpFieldValidity = useCallback(
    (
      field: "cardNumber" | "securityCode" | "expirationDate",
      errorMessages: Array<{ message: string }>
    ) => {
      secureFieldsValidRef.current[field] = errorMessages.length === 0;
    },
    []
  );

  useEffect(() => {
    const fetchClientCards = async () => {
      try {
        const clientId = localStorage.getItem("clientId");
        const userCardResponse = await api.get(`/card/clientId/${clientId}`);
        setClientCard(userCardResponse.data);
      } catch (error) {
        console.error("Error fetching client card:", error);
      }
    };
    fetchClientCards();
  }, []);

  useEffect(() => {
    (async () => {
      const types = await getIdentificationTypes();
      if (types?.length) {
        setIdentificationTypes(types);
        const cpf = types.find((type) => type.id === "CPF");
        setIdentificationType(cpf?.id ?? types[0].id);
      }
    })();
  }, []);

  const handleClickRemoveCard = async (cardId: string) => {
    try {
      if (clientCard) {
        await api.delete(`/card/${cardId}`);
        setClientCard((prevValue) =>
          prevValue.filter((item) => item.id !== cardId)
        );
      }
    } catch (error) {
      console.error("Error deleting card:", error);
    }
  };

  async function handleAddCard() {
    if (!showAddCard) {
      setShowAddCard(true);
      return;
    }

    setError("Salvar cartão está desativado temporariamente.");
    return;

    /*
    // Fluxo de salvar cartão desativado temporariamente.
    if (!cardSurname.trim() || !cardName.trim()) {
      setError("Informe o apelido e o nome no cartão");
      return;
    }

    if (!identificationTypes.length) {
      setError("Aguarde o carregamento dos tipos de documento.");
      return;
    }

    if (!identificationType || !identificationNumber.trim()) {
      setError("Informe o tipo e o número do documento do titular");
      return;
    }

    const idDigits = identificationNumber.replace(/\D/g, "");
    const idRule = identificationTypes.find((type) => type.id === identificationType);
    if (idRule && (idDigits.length < idRule.min_length || idDigits.length > idRule.max_length)) {
      setError("Número de documento inválido para o tipo selecionado");
      return;
    }

    const secureFields = secureFieldsValidRef.current;
    if (
      !secureFields.cardNumber ||
      !secureFields.expirationDate ||
      !secureFields.securityCode
    ) {
      setError("Preencha número, vencimento e CVV do cartão corretamente");
      return;
    }

    try {
      const responseMp = await createCardToken({
        cardholderName: cardName.trim(),
        identificationType,
        identificationNumber: idDigits,
      });

      if (!responseMp) {
        alert("Erro ao adicionar o cartão");
        return;
      }

      const response = await api.post("/card", {
        cardToken: responseMp.id,
        surnameCard: cardSurname.trim(),
        lastFourDigits: responseMp.last_four_digits,
      });
      setError("");
      setClientCard((prevValue) => [...prevValue, response.data]);
      cleanInputs();
    } catch (error) {
      alert("Não foi possível adicionar o cartão");
    }
    */
  }

  function cleanInputs() {
    setCardName("");
    setCardSurname("");
    setIdentificationNumber("");
    secureFieldsValidRef.current = {
      cardNumber: false,
      securityCode: false,
      expirationDate: false,
    };
    setShowAddCard(false);
  }

  return (
    <div className={styles.main_container}>
      <h1>{t(`accountMenu.cards`)}</h1>
      {!showAddCard &&
        clientCard.map((card) => (
          <ClientCard
            key={card.id}
            cardInfo={card}
            handleRemove={handleClickRemoveCard}
          />
        ))}

      {showAddCard && (
        <div className={styles.containerInfoCard}>
          <div className={styles.wrapperInfoCard}>
            <div className={styles.inputCredit2}>
              <label className={styles.inputCredit} htmlFor="cardSurname">
                Apelido do cartão
              </label>
              <input
                id="cardSurname"
                placeholder="Cartão de crédito"
                type="text"
                value={cardSurname}
                onChange={(e) => setCardSurname(e.target.value)}
              />
            </div>
            <div className={styles.inputCredit2}>
              <label className={styles.inputCredit} htmlFor="cardName">
                Nome no cartão
              </label>
              <input
                id="cardName"
                type="text"
                placeholder="João da Silva"
                value={cardName}
                onChange={(e) => setCardName(e.target.value)}
              />
            </div>
          </div>
          <MpCardSecureFields onFieldValidity={onMpFieldValidity} />
          <div className={styles.wrapperInfoCard}>
            <div className={styles.inputCredit2}>
              <label className={styles.inputCredit} htmlFor="identificationType">
                Documento do titular
              </label>
              <select
                id="identificationType"
                className={styles.selectInput}
                value={identificationType}
                onChange={(e) => setIdentificationType(e.target.value)}
              >
                {identificationTypes.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.name}
                  </option>
                ))}
              </select>
            </div>
            <div className={styles.inputCredit2}>
              <label className={styles.inputCredit} htmlFor="identificationNumber">
                Número do documento
              </label>
              <input
                id="identificationNumber"
                placeholder="000.000.000-00"
                type="text"
                inputMode="numeric"
                value={identificationNumber}
                onChange={(e) => setIdentificationNumber(e.target.value)}
              />
            </div>
          </div>
        </div>
      )}

      {clientCard.length === 0 && !showAddCard && (
        <div className={styles.noCardsContainer}>
          <div
            className={styles.iconNoCards}
            style={{
              backgroundImage: `url(${dangerImage})`,
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              width: "7rem",
              height: "7rem",
            }}
          ></div>
          <h3>{t(`accountMenu.noCards`)}</h3>
        </div>
      )}
      <span style={{ color: "#D8372C" }}>{error}</span>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
        }}
      >
        {showAddCard && (
          <button
            className={styles.addCardButton}
            type="button"
            onClick={cleanInputs}
          >
            {t(`EsqueciSenha.voltar`)}
          </button>
        )}
        {!showAddCard && (
          <button
            className={styles.addCardButton}
            type="button"
            onClick={() => handleAddCard()}
          >
            {t(`accountMenu.addNewCard`)}
          </button>
        )}
      </div>

      {/* {showDeleteModal && (
        <DeleteCardModal
          clientCard={clientCard!}
          onCancel={() => setShowDeleteModal(false)}
          onDelete={handleDeleteCard}
        />
      )} */}
    </div>
  );
};

export default ClientCardsList;
